import classNames from 'classnames';

import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@mui/styles';

import RefreshIcon from '@mui/icons-material/Refresh';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Table from '@mui/material/Table';
import TableSortLabel from '@mui/material/TableSortLabel';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconsFilterList from '@mui/icons-material/FilterList';
import { QueryHookOptions } from '@apollo/client';
import { QueryResult } from '@apollo/client/react/types/types';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Chip from '@mui/material/Chip';
import { Theme, lighten } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import useDebounceValue from '../../_hook/useDebounceValue';
import GraphQLTablePaginationActions from './GraphQLTablePaginationActions';
import {
  IPagination,
  IWithPagination,
  QUERY_FILTER_PARAMETER_NAME,
  QUERY_SEARCH_PARAMETER_NAME,
  QUERY_SORT_PARAMETER_NAME,
} from '../../../../graphql/query/_module/baseQueryType';
import ButtonSubmit from '../../_button/ButtonSubmit';

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  toolbar: { width: '100%' },
  paper: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  table: { minWidth: 750 },
  tableWrapper: {
    overflowX: 'auto',
    position: 'relative',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  tableLoadingIcon: {
    marginLeft: theme.spacing(1),
    color: '#fff',
  },
  highlight: {
    ...(theme.palette.mode === 'light'
      ? {
          color: theme.palette.primary.main,
          backgroundColor: lighten(theme.palette.primary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.primary.dark,
        }),
    ...{
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        paddingTop: '10px',
        paddingBottom: '10px',
      },
    },
  },
  title: { flex: '0 0 auto' },
  filterListPaper: {
    padding: theme.spacing(2),
    maxWidth: 400,
    minWidth: 250,
  },
  filterButtonTitle: {
    display: 'unset',
    marginRight: theme.spacing(1),
  },
  filterButtonSearching: {
    display: 'unset',
    marginLeft: theme.spacing(1),
  },
  filterChipList: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  tableLoadingWrapper: {
    position: 'absolute',
    top: 0,
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.3)',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface IData<TData, TItem> {
  // @ts-ignore
  [key: keyof TData]: IWithPagination<TItem>;
}

interface IVars {
  skip?: number;
  limit?: number;
  [QUERY_FILTER_PARAMETER_NAME]: {
    [key: string]: any;
  };
  [QUERY_SORT_PARAMETER_NAME]: {
    [key: string]: any;
  };
  [QUERY_SEARCH_PARAMETER_NAME]: {
    [key: string]: any;
  };
}

interface IProps<TData, TItem, TVars extends IVars> {
  useQuery: (
    fragment: string,
    options?: QueryHookOptions<TData, Partial<IVars>>,
    rootFragment?: string,
    withPagination?: boolean
  ) => QueryResult<TData, Partial<TVars>>;
  selectedItems?: TItem[];
  renderHeader: {
    field: string;
    align: 'inherit' | 'left' | 'center' | 'right' | 'justify';
    size?: 'small' | 'medium';
    width?: string;
    label: string;
    sortable?: boolean;
  }[];
  itemKey: keyof TItem;
  actionButtons?: ReactElement[];
  searchFields?: {
    [key in keyof TVars[typeof QUERY_SEARCH_PARAMETER_NAME]]?: {
      handleValue?: () => any;
      label: string;
      value?: any;
      size?: any;
      component?: any;
      componentProps?: { [key: string]: any };
      onChange?: (value: any) => any;
      custom?: boolean;
    };
  };
  filterFields?: {
    [key in keyof TVars[typeof QUERY_FILTER_PARAMETER_NAME]]?: {
      handleValue?: () => any;
      label: string;
      value?: any;
      size?: any;
      component?: any;
      componentProps?: { [key: string]: any };
      onChange?: (value: any) => any;
      custom?: boolean;
    };
  };
  renderItem: (
    item: TItem,
    checkbox?: React.ReactElement
  ) => React.ReactElement | null;
  noDataComponent?: (
    hasSearch: string,
    resetFilter: () => void
  ) => React.ReactElement;
  refreshKey?: number;
  defaultSort: keyof TVars['sort'];
  filterData?: (item: TItem[]) => TItem[];
  useFilterDataItemCount?: boolean;
  select: string;
  defaultFilterOptions?: {
    [key in keyof TVars[typeof QUERY_FILTER_PARAMETER_NAME]]?: any;
  };
  defaultSearchOptions?: {
    [key in keyof TVars[typeof QUERY_SEARCH_PARAMETER_NAME]]?: any;
  };
  disablePaper?: boolean;
  useCheckbox?: boolean;
  onItemSelected?: (items: TItem[]) => void;
  title: string | React.ReactNode;
  keepShowActionBar?: boolean;
  renderItemWithoutTableRow?: boolean;
  defaultSortOrder?: number;
  ignoreQuery?: boolean;
  tableSize?: 'small' | 'medium';
  useSearchButton?: boolean;
  noSyncSelectedItem?: boolean;
}

export default function GraphQLTable<
  TData extends IData<TData, TItem>,
  TItem,
  TVars extends IVars
>(props: IProps<TData, TItem, TVars>) {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    select,
    useQuery,
    defaultFilterOptions,
    defaultSearchOptions,
    refreshKey,
    disablePaper,
    renderHeader,
    renderItem,
    useCheckbox,
    searchFields,
    filterFields,
    onItemSelected,
    title,
    actionButtons,
    itemKey,
    selectedItems,
    filterData,
    keepShowActionBar,
    renderItemWithoutTableRow,
    defaultSort,
    defaultSortOrder,
    noDataComponent,
    useFilterDataItemCount,
    ignoreQuery,
    tableSize,
    useSearchButton,
    noSyncSelectedItem,
  } = props;
  const filterFieldsWithFilterInfo: Record<string, any> = filterFields || {};

  Object.keys(filterFieldsWithFilterInfo).forEach((key) => {
    filterFieldsWithFilterInfo[key].isFilter = true;
  });

  const searchableFields = {
    ...(searchFields || {}),
    ...filterFieldsWithFilterInfo,
  };
  const [sortOptions, setSortOptions] = useState({
    sortKey: defaultSort,
    sort: defaultSortOrder || -1,
  });
  const [limit, setLimit] = useState(15);
  const [skip, setSkip] = useState<number>(0);
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set());
  const [filterListAnchorEl, setFilterListAnchorEl] = useState<
    undefined | null
  >(undefined);
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: any }>(
    defaultFilterOptions || {}
  );
  const [searchOptions, setSearchOptions] = useState<{ [key: string]: any }>(
    defaultSearchOptions || {}
  );
  // Store temp searchOptions, to debounce withTracker
  const [tempSearchOptions, setTempSearchOptions] = useState<
    Record<string, any>
  >({});
  // Store value for custom component
  const [
    searchableOptionsCustomComponent,
    setSearchOptionsCustomComponent,
  ] = useState<Record<string, any>>({});
  // Store selected item length to determine if selected items modify from parent
  const [selectedItemLength, setSelectedItemLength] = useState(
    selectedItems ? selectedItems.length : 0
  );
  const sort: Record<Partial<keyof TVars['sort']> | any, number> = {};
  const debounceSearchQuery = useDebounceValue(
    JSON.stringify(tempSearchOptions),
    1000
  );

  if (sortOptions.sortKey) {
    sort[sortOptions.sortKey] = sortOptions.sort;
  }

  useEffect(() => {
    setFilterOptions(defaultFilterOptions || {});
  }, [JSON.stringify(defaultFilterOptions)]);

  useEffect(() => {
    setSearchOptions(defaultSearchOptions || {});
  }, [JSON.stringify(defaultSearchOptions)]);

  useEffect(() => {
    if (!useSearchButton) {
      setSearchOptions(tempSearchOptions);
    }
  }, [debounceSearchQuery]);

  Object.keys(filterOptions).forEach((key) => {
    if (filterOptions[key] === '' || filterOptions[key] === null) {
      delete filterOptions[key];
    }
  });

  Object.keys(searchOptions).forEach((key) => {
    if (searchOptions[key] === '' || searchOptions[key] === null) {
      delete searchOptions[key];
    }
  });

  useEffect(() => {
    setSkip(0);
  }, [
    JSON.stringify(filterOptions),
    JSON.stringify(searchOptions),
    sortOptions.sortKey,
    sortOptions.sort,
  ]);

  const exactFilterOptions: Record<string, any> = {};

  Object.keys(filterOptions).forEach((field) => {
    const filterFieldOption = searchableFields[field];
    if (filterFieldOption) {
      const filterValue = filterFieldOption.handleValue
        ? filterFieldOption.handleValue()
        : filterOptions[field];

      if (!filterFieldOption.custom) {
        exactFilterOptions[field] = filterValue;
      }
    } else {
      exactFilterOptions[field] = filterOptions[field];
    }
  });

  const exactSearchOptions: Record<string, any> = {};

  Object.keys(searchOptions).forEach((field) => {
    const searchFieldOption = searchableFields[field];
    const searchValue = searchFieldOption.handleValue
      ? searchFieldOption.handleValue()
      : searchOptions[field];

    if (!searchFieldOption.custom) {
      exactSearchOptions[field] = searchValue;
    }
  });

  const { loading, data: response, refetch } = useQuery(
    select,
    {
      variables: {
        skip,
        limit,
        [QUERY_FILTER_PARAMETER_NAME]: exactFilterOptions,
        [QUERY_SORT_PARAMETER_NAME]: sort,
        [QUERY_SEARCH_PARAMETER_NAME]: exactSearchOptions,
      },
      skip: ignoreQuery,
    },
    undefined,
    true
  );
  let dataKey: keyof TData | null = null;
  let responseData: IWithPagination<TItem> | null = null;
  let data: TItem[] = [];
  let pagination: IPagination | null = null;

  if (response) {
    dataKey = Object.keys(response)[0] as keyof TData;
    if (dataKey) {
      responseData = (response[dataKey] as any) as IWithPagination<TItem>;
      data = responseData.items;
      pagination = responseData.pagination;
    }
  }

  useEffect(() => {
    setSelectedItemKeys(new Set());
    if (onItemSelected) {
      onItemSelected([]);
    }
  }, [loading]);

  useEffect(() => {
    if (refetch) {
      setSelectedItemKeys(new Set());
      if (onItemSelected) {
        onItemSelected([]);
      }
      refetch();
    }
  }, [
    refreshKey,
    skip,
    limit,
    JSON.stringify(exactFilterOptions),
    JSON.stringify(sort),
    JSON.stringify(exactSearchOptions),
  ]);

  useEffect(() => {
    if (!noSyncSelectedItem) {
      if (selectedItems && selectedItemLength !== selectedItems.length) {
        // Sync selectedItems & selectedItemKeys,
        // to maintain consistency, when selectedItems changed.
        // SelectedItems change from outside sync selectedItemKeys
        setSelectedItemLength(selectedItems.length);

        setSelectedItemKeys(
          new Set(
            data
              .filter((item) =>
                selectedItems.find(
                  (selectedItem) => selectedItem[itemKey] === item[itemKey]
                )
              )
              .map((item) => item[itemKey])
          )
        );
      } else {
        // sync selectedItemKeys to outside selectedItems
        if (onItemSelected) {
          onItemSelected(
            data.filter((item) => selectedItemKeys.has(item[itemKey]))
          );
        }

        // ensure selectedItemKeys exist in data
        setSelectedItemKeys(
          new Set(
            data
              .filter((item) => selectedItemKeys.has(item[itemKey]))
              .map((item) => item[itemKey])
          )
        );
      }
    }
  }, [
    selectedItemKeys.size,
    selectedItems ? selectedItems.length !== selectedItemKeys.size : false,
  ]);

  function getTableSortLabel(field: string, label: string, sortable?: boolean) {
    if (!sortable) {
      return label;
    }
    return (
      <TableSortLabel
        active={sortOptions.sortKey === field}
        direction={sortOptions.sort === -1 ? 'desc' : 'asc'}
        onClick={() => {
          setSortOptions({
            sortKey: field,
            sort: sortOptions.sort === -1 ? 1 : -1,
          });
        }}
      >
        {label}
        {sortOptions.sortKey === field && (
          <span className={classes.visuallyHidden}>
            {sortOptions.sort === -1 ? 'sorted descending' : 'sorted ascending'}
          </span>
        )}
      </TableSortLabel>
    );
  }

  function handleFilterListOpen(event: any) {
    setFilterListAnchorEl(event.currentTarget);
  }

  function handleFilterListClose() {
    setFilterListAnchorEl(null);
  }

  function updateSearchableFieldValue(field: string, value: any) {
    const fieldData = searchableFields[field];

    if (fieldData.component) {
      setSearchOptionsCustomComponent({
        ...searchableOptionsCustomComponent,
        ...{
          [field]: value,
        },
      });

      if (fieldData.isFilter) {
        setFilterOptions({
          ...filterOptions,
          ...{
            [field]: fieldData.onChange ? fieldData.onChange(value) : value,
          },
        });
      } else {
        setTempSearchOptions({
          ...tempSearchOptions,
          ...{
            [field]: fieldData.onChange ? fieldData.onChange(value) : value,
          },
        });

        if (useSearchButton && value === '') {
          setSearchOptions({
            ...searchOptions,
            ...{
              [field]: fieldData.onChange ? fieldData.onChange(value) : value,
            },
          });
        }
      }
    } else {
      if (fieldData.isFilter) {
        setFilterOptions({
          ...filterOptions,
          ...{
            [field]: fieldData.onChange ? fieldData.onChange(value) : value,
          },
        });
      } else {
        setTempSearchOptions({
          ...tempSearchOptions,
          ...{ [field]: value },
        });
        if (useSearchButton && value === '') {
          setSearchOptions({
            ...searchOptions,
            ...{ [field]: value },
          });
        }
      }
    }
  }

  function resetFilter() {
    setTempSearchOptions({});
    setSearchOptions({});
    setSearchOptionsCustomComponent({});
    setFilterOptions(defaultFilterOptions || {});
  }

  useEffect(() => {
    if (searchableFields) {
      Object.keys(searchableFields).forEach((field) => {
        const { value } = searchableFields[field];

        if (value !== undefined) {
          updateSearchableFieldValue(field, value);
        }
      });
    }
  }, [
    searchableFields
      ? JSON.stringify(
          Object.values(searchableFields).map(
            (searchableField) => searchableField.value
          )
        )
      : null,
  ]);

  const numSelected =
    noSyncSelectedItem && selectedItems
      ? selectedItems.length
      : selectedItemKeys.size;
  const fadeTimeout = 320;
  const showFilter = Boolean(
    searchableFields && Object.keys(searchableFields).length
  );
  const hasSearch = Object.values(tempSearchOptions).join('');
  const isShowActionBar =
    numSelected > 0 ||
    (selectedItems && selectedItems.length > 0) ||
    keepShowActionBar;

  if (filterData) {
    data = filterData(data);
  }

  const table = (
    <>
      {(title || showFilter) && (
        <Toolbar
          className={classNames(classes.toolbar, {
            [classes.highlight]: isShowActionBar,
          })}
        >
          <div className={classes.title}>
            <Fade
              in={isShowActionBar}
              timeout={fadeTimeout}
              style={{ display: isShowActionBar ? 'block' : 'none' }}
            >
              <Typography color="inherit" variant="subtitle1">
                {numSelected} {t('selected')}
              </Typography>
            </Fade>
            <Fade
              in={!isShowActionBar}
              timeout={fadeTimeout}
              style={{ display: !isShowActionBar ? 'block' : 'none' }}
            >
              <div>
                <Grid container alignItems="center">
                  <Typography variant="h6" id="tableTitle" display="inline">
                    {title}&nbsp;
                    {pagination &&
                      pagination.currentPage !== 1 &&
                      `- ${t('page')} ${pagination.currentPage}`}
                    &nbsp;
                  </Typography>
                  <Button
                    color="inherit"
                    onClick={() => {
                      if (refetch) {
                        refetch();
                      }
                    }}
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </Button>
                </Grid>
              </div>
            </Fade>
          </div>
          <Grid container justifyContent="flex-end" spacing={1}>
            <Fade
              in={isShowActionBar || keepShowActionBar}
              timeout={fadeTimeout}
              style={{ display: isShowActionBar ? 'inherit' : 'none' }}
            >
              <Grid container item spacing={1} justifyContent="flex-end">
                {actionButtons &&
                  actionButtons.map((button, index) => (
                    <Grid item key={button.key}>
                      {button}
                    </Grid>
                  ))}
              </Grid>
            </Fade>
            {showFilter && (
              <Fade
                in={!isShowActionBar}
                timeout={fadeTimeout}
                style={{ display: !isShowActionBar ? 'inherit' : 'none' }}
              >
                <span>
                  <IconButton onClick={handleFilterListOpen} size="large">
                    <IconsFilterList />
                  </IconButton>
                  <Popover
                    open={Boolean(filterListAnchorEl)}
                    anchorEl={filterListAnchorEl}
                    onClose={handleFilterListClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                  >
                    <Paper className={classes.filterListPaper} elevation={0}>
                      <Grid container>
                        <Grid container item xs={12} alignItems="center">
                          <Typography
                            variant="button"
                            className={classes.filterButtonTitle}
                          >
                            {t('filter')}
                          </Typography>
                          <Button
                            color="primary"
                            onClick={(e) => {
                              e.persist();
                              resetFilter();
                            }}
                          >
                            {t('reset')}
                          </Button>
                          {loading && (
                            <>
                              <Typography
                                variant="button"
                                color="primary"
                                className={classes.filterButtonSearching}
                              >
                                {t('searching')}&nbsp;
                              </Typography>
                              <CircularProgress size={12} />
                            </>
                          )}
                        </Grid>
                        <Grid container spacing={2}>
                          {Object.keys(searchableFields).map((field) => {
                            const fieldData = searchableFields[field];
                            const propsData = {
                              label: fieldData.label,
                              fullWidth: true,
                            };

                            return (
                              <Grid key={field} item xs={fieldData.size || 6}>
                                {fieldData.component ? (
                                  React.createElement(fieldData.component, {
                                    ...propsData,
                                    ...(fieldData.componentProps || {}),
                                    ...{
                                      value:
                                        searchableOptionsCustomComponent[field],
                                      disabled: loading,
                                      onChange: (value: any) => {
                                        updateSearchableFieldValue(
                                          field,
                                          value
                                        );
                                      },
                                    },
                                  })
                                ) : (
                                  <TextField
                                    label={propsData.label}
                                    fullWidth={propsData.fullWidth}
                                    disabled={loading}
                                    value={tempSearchOptions[field] || ''}
                                    onChange={(e) => {
                                      e.persist();
                                      updateSearchableFieldValue(
                                        field,
                                        e.target.value
                                      );
                                    }}
                                  />
                                )}
                              </Grid>
                            );
                          })}
                          {useSearchButton && (
                            <Grid item xs={12}>
                              <ButtonSubmit
                                fullWidth
                                isSubmitting={loading}
                                variant="contained"
                                label={t('search')}
                                color="primary"
                                submittingLabel={t('searching')}
                                icon={<SearchIcon fontSize="small" />}
                                onClick={() =>
                                  setSearchOptions(tempSearchOptions)
                                }
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Popover>
                </span>
              </Fade>
            )}
          </Grid>
        </Toolbar>
      )}
      <Grid container spacing={1} className={classes.filterChipList}>
        {pagination && pagination.currentPage !== 1 && (
          <Grid item>
            <Chip
              label={`${t('page')}: ${pagination.currentPage}`}
              onDelete={() => {
                setSkip(0);
              }}
              color="primary"
            />
          </Grid>
        )}
        {Object.keys(searchableFields).map((field) => {
          const fieldData = searchableFields[field];
          const { label } = fieldData;
          let value = fieldData.component
            ? filterOptions[field]
            : searchOptions[field];

          if (
            searchableOptionsCustomComponent[field] &&
            searchableOptionsCustomComponent[field].label
          ) {
            value = searchableOptionsCustomComponent[field].label;
          }

          if (value === '' || value === undefined || value === null) {
            return null;
          }

          return (
            <Grid key={field} item>
              <Chip
                label={`${label}: ${value}`}
                onDelete={() => {
                  updateSearchableFieldValue(field, '');
                }}
                color="primary"
              />
            </Grid>
          );
        })}
      </Grid>
      <div className={classes.tableWrapper}>
        <TableContainer>
          <Table size={tableSize || 'medium'} className={classes.table}>
            <TableHead>
              <TableRow>
                {useCheckbox && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedItemKeys.size > 0 &&
                        selectedItemKeys.size < data.length
                      }
                      checked={selectedItemKeys.size === data.length}
                      onChange={(event) => {
                        if (event.target.checked) {
                          setSelectedItemKeys(
                            new Set(data.map((item) => item[itemKey]))
                          );
                        } else {
                          setSelectedItemKeys(new Set());
                        }
                      }}
                    />
                  </TableCell>
                )}
                {renderHeader.map((header) => (
                  <TableCell
                    key={header.field}
                    align={header.align || 'left'}
                    size={header.size || 'medium'}
                    style={header.width ? { width: header.width } : {}}
                    sortDirection={sortOptions.sort === -1 ? 'desc' : 'asc'}
                  >
                    {getTableSortLabel(
                      header.field,
                      header.label,
                      header.sortable
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item) => {
                const checkbox = (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={selectedItemKeys.has(item[itemKey])}
                      onChange={(event) => {
                        const newSelectedItemKeys = new Set(
                          Array.from(selectedItemKeys)
                        );

                        if (event.target.checked) {
                          newSelectedItemKeys.add(item[itemKey]);
                        } else {
                          newSelectedItemKeys.delete(item[itemKey]);
                        }
                        setSelectedItemKeys(newSelectedItemKeys);
                      }}
                    />
                  </TableCell>
                );

                if (renderItemWithoutTableRow) {
                  return renderItem(item, checkbox) || null;
                }

                const itemContent = renderItem(item);

                if (!itemContent) {
                  return null;
                }

                return (
                  <TableRow key={item[itemKey] as any}>
                    {useCheckbox && checkbox}
                    {itemContent}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <div className={classes.tableLoadingWrapper}>
            <CircularProgress
              className={classes.tableLoadingIcon}
              size={32}
              color="inherit"
            />
          </div>
        )}

        {!loading && !data.length && (
          <Grid container justifyContent="center" spacing={1}>
            <Grid item xs={12}>
              <Typography align="center" color="inherit" variant="h6">
                {t('no_matching_results')}
              </Typography>
            </Grid>
            {noDataComponent && noDataComponent(hasSearch, resetFilter)}
            {hasSearch && (
              <Grid container item xs={12} justifyContent="center">
                <Button
                  color="primary"
                  onClick={() => {
                    resetFilter();
                  }}
                >
                  {t('reset_filter')}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        {pagination && (
          <TablePagination
            rowsPerPageOptions={[15, 30, 50]}
            component="div"
            count={
              filterData && useFilterDataItemCount
                ? data.length
                : pagination.total
            }
            rowsPerPage={limit}
            onRowsPerPageChange={(event) =>
              setLimit(parseInt(event.target.value, 10))
            }
            page={pagination.currentPage - 1}
            onPageChange={(event, newPage) => setSkip(limit * newPage)}
            ActionsComponent={GraphQLTablePaginationActions}
          />
        )}
      </div>
    </>
  );

  if (disablePaper) {
    return table;
  }

  return <Paper className={classNames(classes.paper)}>{table}</Paper>;
}
